import { useCallback, useState } from 'react';

import {
	resyncPassengerBookings,
	useBusinessLayer,
} from '@mopla/business-logic';
import { CancellationReasonPassengerBookingEnum } from '@mopla/data-models';
import { useLogger } from '@mopla/logger';

interface ICancelBookingPayload {
	bookingNumber: string;
	cancellationReason: CancellationReasonPassengerBookingEnum | string;
	cancellationReasonDescription?: string;
}

/**
 * Shared cancellation request used by {CancellationFlow} (for both the regular
 * reason-form path and the school confirm-only path). Owns the POST, the RxDB
 * resync, error reporting and the `isCancelling` state. Returns `true` on
 * success so the caller can drive its own step machine.
 */
export const useCancelBookingRequest = () => {
	const bl = useBusinessLayer();
	const logger = useLogger('useCancelBookingRequest');
	const [isCancelling, setIsCancelling] = useState(false);

	const cancelBooking = useCallback(
		async (payload: ICancelBookingPayload): Promise<boolean> => {
			setIsCancelling(true);
			try {
				await bl.api.post('/api/command/cancelBooking', {
					/** The `cancelBooking` command's `bookingId` field actually expects
					 *  the booking NUMBER (the shorter human-facing id). */
					bookingId: payload.bookingNumber,
					cancellationReason: payload.cancellationReason,
					cancellationReasonDescription:
						payload.cancellationReasonDescription || undefined,
				});
				bl.dispatch(resyncPassengerBookings());
				return true;
			} catch (e) {
				logger.error('Failed to cancel booking', e);
				return false;
			} finally {
				setIsCancelling(false);
			}
		},
		[bl, logger]
	);

	return { cancelBooking, isCancelling };
};
